import { Dropdown } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useChannelCards } from '@entities/channel';
import {
  useDlgDetail,
  useDlgList,
  useDlgSummary,
  type DlgItem,
  type DlgPlatform,
} from '@entities/dialogs';
import { extractErrorMessage } from '@shared/api';
import { LetterAvatar, num, plural, shortWhen } from '@shared/ui/v2';
import { DialogsChart } from './DialogsChart';
import './dialogs.css';

const PERIODS = [
  { days: 1, label: 'Сутки' },
  { days: 7, label: '7 дней' },
  { days: 30, label: '30 дней' },
  { days: 90, label: '90 дней' },
  { days: 365, label: 'Год' },
  { days: 3650, label: 'Всё время' },
];
const CHART_KEY = 'richat.dialogs.chart';

function readChart() {
  try {
    return localStorage.getItem(CHART_KEY) === '1';
  } catch {
    return false;
  }
}

function useDebounced<T>(v: T, ms: number) {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}

const MSK = 'Europe/Moscow';
const time = (iso: string) =>
  new Date(iso).toLocaleTimeString('ru-RU', { timeZone: MSK, hour: '2-digit', minute: '2-digit' });
function started(iso: string) {
  const d = new Date(iso);
  const day = (x: Date) => x.toLocaleDateString('ru-RU', { timeZone: MSK });
  const now = new Date();
  if (day(d) === day(now)) return `сегодня в ${time(iso)}`;
  if (day(d) === day(new Date(now.getTime() - 86_400_000))) return `вчера в ${time(iso)}`;
  return `${d.toLocaleDateString('ru-RU', { timeZone: MSK, day: 'numeric', month: 'long' })} в ${time(iso)}`;
}
const platformName = (p: string) =>
  p === 'widget' ? 'Сайт' : p === 'telegram' ? 'Telegram' : 'MAX';

/** Экран 8 «Ассистент → Диалоги» (утверждён 28.10.2026). */
export function DialogsPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [sp, setSp] = useSearchParams();
  const channelId = sp.get('channel');
  const [platform, setPlatform] = useState<DlgPlatform>('all');
  const [days, setDays] = useState(7);
  const [q, setQ] = useState('');
  const [onlyUa, setOnlyUa] = useState(false);
  const [chartOpen, setChartOpen] = useState(readChart);
  const [selId, setSelId] = useState<string | null>(null);
  const [mobileConv, setMobileConv] = useState(false);
  const qd = useDebounced(q.trim(), 350);
  const { data: channels } = useChannelCards(id);
  const channel = channels?.find((c) => c.id === channelId);

  const filter = { platform, channelId, days };
  const { data: summary } = useDlgSummary(id, filter);
  const list = useDlgList(id, { ...filter, q: qd, unanswered: onlyUa });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p.items) ?? [], [list.data]);
  const total = list.data?.pages[0]?.total ?? 0;
  const sel = items.find((d) => d.id === selId) ?? items[0];
  const {
    data: detail,
    isLoading: detailLoading,
    error: detailErr,
  } = useDlgDetail(id, sel?.id ?? null);

  // Подгрузка при прокрутке списка — порциями по 50.
  const sentinel = useRef<HTMLDivElement>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = list;
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
    });
    io.observe(el);
    return () => io.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, items.length]);

  const toggleChart = () => {
    setChartOpen((v) => {
      try {
        localStorage.setItem(CHART_KEY, v ? '0' : '1');
      } catch {
        /* не запомним — не страшно */
      }
      return !v;
    });
  };

  const periodLabel = PERIODS.find((p) => p.days === days)?.label ?? `${days} дней`;
  const listHint =
    total > items.length
      ? `Показаны последние ${num(items.length)} из ${num(total)} · при прокрутке подгрузим ещё`
      : `${onlyUa ? 'Без ответа' : 'Все диалоги'} за период: ${num(total)}`;

  const pick = (d: DlgItem) => {
    setSelId(d.id);
    setMobileConv(true);
  };

  return (
    <>
      <header className="v2-topbar is-section">
        <h1>Диалоги</h1>
        <span className="v2-topbar-hint">Переписка клиентов с ассистентом во всех каналах</span>
        <div className="v2-topbar-actions">
          <div className="dl-seg" role="radiogroup" aria-label="Канал">
            {(
              [
                ['all', 'Все каналы'],
                ['widget', 'Сайт'],
                ['telegram', 'Telegram'],
                ['max', 'MAX'],
              ] as const
            ).map(([v, t]) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={platform === v}
                className={platform === v ? 'is-on' : undefined}
                onClick={() => setPlatform(v)}
              >
                {t}
              </button>
            ))}
          </div>
          <Dropdown
            trigger={['click']}
            menu={{
              selectedKeys: [String(days)],
              items: PERIODS.map((p) => ({ key: String(p.days), label: p.label })),
              onClick: ({ key }) => setDays(Number(key)),
            }}
          >
            <button type="button" className="v2-btn" aria-label={`Период: ${periodLabel}`}>
              {periodLabel} ▾
            </button>
          </Dropdown>
        </div>
      </header>

      <div className="v2-content is-section dl-wrap">
        <section className="v2-card dl-summary" aria-label="Сводка за период">
          <div className="dl-sum-row">
            <span className="dl-stat" title="Диалогов за период">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <path d="M4 5h16v11H8l-4 4z" />
              </svg>
              <b>{num(summary?.dialogs)}</b>
              <span className="v2-sr">диалогов</span>
            </span>
            <span className="dl-stat" title="Клиентов за период">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6" />
              </svg>
              <b>{num(summary?.clients)}</b>
              <span className="v2-sr">клиентов</span>
            </span>
            <span className="dl-stat" title="Сообщений за период">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <rect x="3" y="5" width="18" height="14" rx="2" />
                <path d="M3 7l9 6 9-6" />
              </svg>
              <b>{num(summary?.messages)}</b>
              <span className="v2-sr">сообщений</span>
            </span>
            {!!summary?.unanswered && (
              <button
                type="button"
                className={onlyUa ? 'dl-ua is-on' : 'dl-ua'}
                aria-pressed={onlyUa}
                onClick={() => setOnlyUa((v) => !v)}
                title="Ассистент ответил «нет информации» — показать только такие диалоги"
              >
                ⚠ <b>{num(summary.unanswered)}</b> без ответа
              </button>
            )}
            <span style={{ flex: 1 }} />
            <button
              type="button"
              className="dl-chart-btn"
              aria-expanded={chartOpen}
              onClick={toggleChart}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                aria-hidden="true"
              >
                <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
              </svg>
              График {chartOpen ? '▴' : '▾'}
            </button>
          </div>
          {chartOpen && <DialogsChart aid={id} platform={platform} channelId={channelId} />}
        </section>

        <div className={mobileConv ? 'dl-split is-conv' : 'dl-split'}>
          <section className="v2-card dl-list" aria-label="Список диалогов">
            <div className="dl-list-head">
              <label className="v2-sr" htmlFor="dl-q">
                Поиск по имени или тексту
              </label>
              <input
                id="dl-q"
                className="st-input"
                placeholder="Поиск по имени или тексту"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <label className="dl-check">
                <input
                  type="checkbox"
                  checked={onlyUa}
                  onChange={(e) => setOnlyUa(e.target.checked)}
                />
                Только без ответа
              </label>
              {channel && (
                <span className="dl-chip">
                  Только {channel.platform === 'widget' ? 'окно чата' : channel.title}
                  <button
                    type="button"
                    aria-label="Показать все каналы"
                    onClick={() => {
                      sp.delete('channel');
                      setSp(sp, { replace: true });
                    }}
                  >
                    ✕
                  </button>
                </span>
              )}
            </div>
            <div className="dl-list-hint v2-xs v2-muted">{listHint}</div>
            <div className="dl-items">
              {list.error && (
                <div className="v2-card-pad v2-small">
                  Не удалось загрузить диалоги: {extractErrorMessage(list.error)}
                </div>
              )}
              {!list.isLoading && !items.length && !list.error && (
                <div className="v2-card-pad v2-small v2-muted">
                  {qd || onlyUa ? 'Ничего не нашлось.' : 'За этот период диалогов нет.'}
                </div>
              )}
              {items.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={d.id === sel?.id ? 'dl-item is-sel' : 'dl-item'}
                  onClick={() => pick(d)}
                >
                  <LetterAvatar name={d.name} colorKey={d.name} size={30} />
                  <span className="dl-item-main">
                    <span className="dl-item-top">
                      <b>{d.name}</b>
                      <span className="v2-xs v2-muted">{shortWhen(d.last_at)}</span>
                    </span>
                    <span className="dl-item-text">{d.last_text}</span>
                    <span className="v2-xs v2-muted">
                      {d.channel_title} · {d.messages} сообщ.
                      {d.unanswered && <span className="dl-ua-tag"> · без ответа</span>}
                    </span>
                  </span>
                </button>
              ))}
              <div ref={sentinel} style={{ height: 1 }} />
              {list.isFetchingNextPage && (
                <div className="v2-card-pad v2-xs v2-muted">Загружаем…</div>
              )}
            </div>
          </section>

          <section className="v2-card dl-conv" aria-label="Переписка">
            {!sel && <div className="v2-card-pad v2-muted v2-small">Выберите диалог слева.</div>}
            {sel && (
              <>
                <div className="dl-conv-head">
                  <button
                    type="button"
                    className="v2-btn dl-back"
                    onClick={() => setMobileConv(false)}
                  >
                    ← Все диалоги
                  </button>
                  <LetterAvatar name={sel.name} colorKey={sel.name} size={34} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{detail?.name ?? sel.name}</div>
                    <div className="v2-xs v2-muted">
                      {platformName(sel.platform)}
                      {sel.platform !== 'widget' ? ` · ${sel.channel_title}` : ''}
                      {detail
                        ? ` · начат ${started(detail.started_at)} · ${num(detail.messages_count)} ${plural(detail.messages_count, 'сообщение', 'сообщения', 'сообщений')}`
                        : ''}
                    </div>
                  </div>
                </div>
                <div className="dl-msgs">
                  {detailLoading && <div className="v2-muted v2-small">Загружаем переписку…</div>}
                  {detailErr && (
                    <div role="alert" className="v2-small">
                      Не удалось загрузить переписку: {extractErrorMessage(detailErr)}
                    </div>
                  )}
                  {detail?.truncated && (
                    <div className="v2-xs v2-muted" style={{ textAlign: 'center' }}>
                      Показаны последние 500 сообщений
                    </div>
                  )}
                  {detail?.messages.map((m) =>
                    m.direction === 'incoming' ? (
                      <div key={m.id} className="dl-msg is-in">
                        <div className="dl-bubble">{m.content}</div>
                        <div className="v2-xs v2-muted">{time(m.created_at)}</div>
                      </div>
                    ) : (
                      <div key={m.id} className="dl-msg is-out">
                        <div className={m.no_answer ? 'dl-bubble is-na' : 'dl-bubble'}>
                          {m.content}
                        </div>
                        <div className="v2-xs v2-muted">
                          {time(m.created_at)}
                          {m.sources.map((s) => (
                            <span key={`${s.name}-${s.page}-${s.url}`}>
                              {' · '}
                              {s.url ? (
                                <a href={s.url} target="_blank" rel="noreferrer noopener">
                                  {s.name}
                                </a>
                              ) : (
                                s.name
                              )}
                              {s.page ? ` · стр. ${s.page}` : ''}
                            </span>
                          ))}
                        </div>
                        {m.no_answer && (
                          <Link to={`/assistants/${id}/knowledge`} className="dl-na-link">
                            Ассистент не нашёл ответ — добавить в базу знаний →
                          </Link>
                        )}
                      </div>
                    ),
                  )}
                </div>
                <div className="dl-conv-foot v2-xs v2-muted">
                  Это история переписки — отвечает ассистент. Изменить ответы можно через{' '}
                  <Link to={`/assistants/${id}/settings`}>инструкцию</Link> и{' '}
                  <Link to={`/assistants/${id}/knowledge`}>базу знаний</Link>.
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
